import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, MessageCircle, Send, CheckCircle2, AlertCircle } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Switch } from "@/shared/ui/switch";
import { Button } from "@/shared/ui/button";
import { Separator } from "@/shared/ui/separator";
import { Badge } from "@/shared/ui/badge";
import { toast } from "sonner";

type Settings = {
  user_created_enabled: boolean;
  user_deleted_enabled: boolean;
  subscription_created_enabled: boolean;
  subscription_renewed_enabled: boolean;
  subscription_cancelled_enabled: boolean;
  subscription_refunded_enabled: boolean;
  has_edge_function_url: boolean;
  has_webhook_secret: boolean;
  webhook_available: boolean;
  connected: boolean;
};

const EVENT_TOGGLES: { key: keyof Settings; titleKey: string; descKey: string; emoji: string }[] = [
  { key: "user_created_enabled", titleKey: "userCreated", descKey: "userCreatedDesc", emoji: "🆕" },
  { key: "user_deleted_enabled", titleKey: "userDeleted", descKey: "userDeletedDesc", emoji: "🗑️" },
  { key: "subscription_created_enabled", titleKey: "subscriptionCreated", descKey: "subscriptionCreatedDesc", emoji: "💳" },
  { key: "subscription_renewed_enabled", titleKey: "subscriptionRenewed", descKey: "subscriptionRenewedDesc", emoji: "🔄" },
  { key: "subscription_cancelled_enabled", titleKey: "subscriptionCancelled", descKey: "subscriptionCancelledDesc", emoji: "❌" },
  { key: "subscription_refunded_enabled", titleKey: "subscriptionRefunded", descKey: "subscriptionRefundedDesc", emoji: "💸" },
];

export default function DiscordIntegrationPage() {
  const { t } = useTranslation("discord");
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [setupRunning, setSetupRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("discord-notify", {
      body: { action: "status" },
    });
    if (error || (data as any)?.error) {
      toast.error(t("loadError"));
    } else if ((data as any)?.settings) {
      const nextSettings = (data as any).settings as Settings;
      setSettings(nextSettings);
      // Auto-setup if endpoint not yet stored
      if (!nextSettings.has_edge_function_url || !nextSettings.has_webhook_secret) {
        await runSetup();
      }
    }
    setLoading(false);
  };

  const runSetup = async () => {
    setSetupRunning(true);
    const { error, data } = await supabase.functions.invoke("discord-notify", {
      body: { action: "setup" },
    });
    if (error || (data as any)?.error) {
      toast.error(t("setupError"));
    } else {
      toast.success(t("setupSuccess"));
      const { data: refreshed } = await supabase.functions.invoke("discord-notify", {
        body: { action: "status" },
      });
      if ((refreshed as any)?.settings) setSettings((refreshed as any).settings as Settings);
    }
    setSetupRunning(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (key: keyof Settings, value: boolean) => {
    if (!settings) return;
    const previous = settings[key];
    setSettings({ ...settings, [key]: value });
    setSaving(key);
    const { error } = await supabase
      .from("discord_settings")
      .update({ [key]: value, updated_at: new Date().toISOString() })
      .eq("id", true);
    setSaving(null);
    if (error) {
      setSettings({ ...settings, [key]: previous });
      toast.error(t("saveError"));
    }
  };

  const sendTest = async () => {
    setTesting(true);
    const { error, data } = await supabase.functions.invoke("discord-notify", {
      body: { action: "test" },
    });
    if (error || (data as any)?.error) {
      toast.error(t("testError"));
    } else {
      toast.success(t("testSuccess"));
    }
    setTesting(false);
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isConnected = !!settings?.connected;

  return (
    <div className="container mx-auto max-w-3xl space-y-6 py-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <MessageCircle className="h-6 w-6 text-indigo-500" />
            {t("title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("subtitle")}</p>
        </div>
        {isConnected ? (
          <Badge variant="secondary" className="gap-1 bg-emerald-500/10 text-emerald-600">
            <CheckCircle2 className="h-3.5 w-3.5" /> {t("connected")}
          </Badge>
        ) : (
          <Badge variant="secondary" className="gap-1 bg-amber-500/10 text-amber-600">
            <AlertCircle className="h-3.5 w-3.5" /> {t("notConfigured")}
          </Badge>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("statusTitle")}</CardTitle>
          <CardDescription>{t("statusDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Button onClick={sendTest} disabled={testing || !isConnected}>
            {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            {t("sendTest")}
          </Button>
          <Button variant="outline" onClick={runSetup} disabled={setupRunning}>
            {setupRunning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("reconfigure")}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("eventsTitle")}</CardTitle>
          <CardDescription>{t("eventsDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="divide-y">
          {EVENT_TOGGLES.map((ev, idx) => (
            <div key={ev.key} className="flex items-center justify-between gap-4 py-4">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <span className="text-base">{ev.emoji}</span>
                  {t(ev.titleKey)}
                </div>
                <p className="text-xs text-muted-foreground">{t(ev.descKey)}</p>
              </div>
              <div className="flex items-center gap-2">
                {saving === ev.key && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                <Switch
                  checked={!!settings?.[ev.key]}
                  onCheckedChange={(v) => toggle(ev.key, v)}
                  disabled={saving === ev.key}
                />
              </div>
              {idx === 1 && <Separator className="hidden" />}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
